import toArray from 'lodash-es/toArray';
import throttle from 'lodash-es/throttle';
import forEach from 'lodash-es/forEach';
import toNumber from 'lodash-es/toNumber';
import { addScript } from '../../bundles/helpers/add-script';

/***
 * Класс поиска по фильтрам
 * @example
 *
 * Методы:
 * update() - обновление поиска, переключает видимость поиска
 *
 * Аттрибуты:
 * data-min-count - {number|null, default = 10} - минимальное число элементов для показа поиска
 *
 * Аттрибуты на элементах:
 * data-input - input для ввода
 * data-list - список параметров
 * data-item - параметр
 * data-not-found - если ничего не найдено, отображается этот элемент
 *
 * Классы:
 * [data-item].hide - скрывает элемент из выдачи при поиске так как не соответствует поисковой строке
 * [data-item].d-none - убирает элемент из поиска, так как является не подходящим фильтром
 * .filter-search_not-found - вешается если поиск не дал результатов
 */
class FilterSearch {
	/***
	 * Конструктор класса
	 * @param elem {HTMLElement} DOM элемент с классом .filter-search
	 */
  constructor(elem) {
    if (!elem) return this.error('Отсутствует elem')
    this.block = elem;
    this.block.FilterSearch = this;

    this.init();
  }

  init = async () => {
		this.inputContainer = this.block.querySelector('.filter-search__input');
		if (!this.inputContainer) return this.error('Отсутствует .filter-search__input');

		this.input = this.block.querySelector('[data-input]');
		if (!this.input) return this.error('Отсутствует [data-input]');

		this.list = this.block.querySelector('[data-list]');
		if (!this.list) return this.error('Отсутствует [data-list]');

		this.items = toArray(this.list.querySelectorAll('[data-item]'));

		this.form = this.block.closest('form');

		this.minCount = toNumber(this.block.dataset['minCount']) || 10;

		if (!window.Mark) await addScript(PUBLIC_PATH + 'vendor/mark.vendor.js');
		this.markIns = new Mark(this.list);

		this.input.addEventListener('input', this.inputHandler);
		this.input.addEventListener('change', this.inputHandler);
		this.form?.addEventListener('reset', this.reset);

		this.inputHandler();
		this.update();

    this.block.classList.add('filter-search_inited');
  }

	inputHandler = throttle(() => {
		const searchValue = this.input.value || "";
		this.markIns.unmark({
			done: () => {
				this.markIns.mark(searchValue, {
					separateWordSearch: false,
					exclude: [
						'.invalid-feedback',
						'.form-check.d-none .form-check-label',
					],
					done: this.done,
				});
			}
		});
	}, 150);

	done = (count) => {
		const toggle = !!(this.input.value && count <= 0);
		this.block.classList.toggle('filter-search_not-found', toggle);

		if (!toggle) this.toggleItems();
	}

	reset = () => {
		this.input.value = this.input.defaultValue;
		this.inputHandler();
	}

	toggleItems = () => {
		forEach(this.items, (item) => {
			const toggle = this.input.value ? !!item.querySelector('.form-check-label mark') : true;
			item.classList.toggle('hide', !toggle);
		})
	}

	updateCount = () => this.count = toArray(this.block.querySelectorAll('[data-item]:not(.d-none)')).length;

	/*** Обновляет видимость поиска */
	update = () => {
		this.updateCount();
		const toggle = this.count < this.minCount;
		this.inputContainer.classList.toggle('d-none', toggle);
		if (toggle) {
			this.markIns.unmark();
			this.input.value = '';
			this.block.classList.remove('filter-search_not-found');
			this.toggleItems();
		}
	}

  destroy = () => {
		if (this.form) this.form.removeEventListener('reset', this.reset);
		if (this.input) {
			this.input.removeEventListener('input', this.inputHandler);
			this.input.removeEventListener('change', this.inputHandler);
		}
    if (this.block) {
      this.block.classList.remove('filter-search_inited');
      delete this.block.FilterSearch;
    }
  }

  errorMessage = (messageText) => {
    console.error(`FilterSearch: ${messageText}`)
  }

  error = (messageText) => {
    this.errorMessage(messageText);
    this.destroy();
    return null;
  }

  static init() {
    Array.from(document.querySelectorAll('.filter-search:not(.filter-search_inited)')).forEach((elem) => new FilterSearch(elem));
  }
}

document.addEventListener('DOMContentLoaded', FilterSearch.init);

// Экспорт;
window.Block = window.Block ? window.Block : {};
window.Block.FilterSearch = FilterSearch;
